import { useCallback, useMemo } from 'react';

import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { useStoreContext } from 'features/store';

import { ServiceMode as GatewayServiceMode } from 'generated/graphql-gateway';
import { usePriceOrder } from 'hooks/price-order/use-price-order';
import { useAuthContext } from 'state/auth';
import { useSanitizedRewards } from 'state/cart/hooks/use-sanitized-rewards';
import { useTransformedAppliedOffers } from 'state/loyalty/hooks/use-transformed-applied-offers';
import { useOrderContext } from 'state/order';
import { ServiceMode } from 'state/service-mode/types';

import { useCheckoutContext } from './use-checkout-context';

/**
 * Checkout V2 helper to (re)price the current order using the legacy `priceOrder` mutation.
 *
 * Used when switching service mode on the checkout screen while Minimal Cart UI is enabled,
 * so totals/serverOrder stay aligned with the newly selected service mode.
 */
export const useCheckoutPriceOrder = () => {
  const enableMinimalCartUi = useMinimalCartUi();
  const { store, isStoreOpenAndAvailable } = useStoreContext();
  const { user } = useAuthContext();
  const { deliveryAddress, deliveryInstructions, orderPhoneNumber } = useOrderContext();
  const appliedOffers = useTransformedAppliedOffers();

  const { cartEntries, serverOrder } = useCheckoutContext();
  const rewardsApplied = useSanitizedRewards({ cartEntries });
  const { priceOrder } = usePriceOrder({ cartEntries });

  const priceOrderStoreParam = useMemo(
    () => ({
      number: store.number,
      posRestaurantId: store.posRestaurantId,
      pos: {
        vendor: store.pos?.vendor,
      },
      vatNumber: store.vatNumber,
      physicalAddress: {
        address1: store.physicalAddress?.address1,
        address2: store.physicalAddress?.address2,
        city: store.physicalAddress?.city,
        country: store.physicalAddress?.country,
        postalCode: store.physicalAddress?.postalCode,
        stateProvince: store.physicalAddress?.stateProvince,
      },
      phoneNumber: store.phoneNumber,
    }),
    [
      store.number,
      store.posRestaurantId,
      store.pos?.vendor,
      store.vatNumber,
      store.phoneNumber,
      store.physicalAddress?.address1,
      store.physicalAddress?.address2,
      store.physicalAddress?.city,
      store.physicalAddress?.country,
      store.physicalAddress?.postalCode,
      store.physicalAddress?.stateProvince,
    ]
  );

  const customerPhoneNumber = useMemo(() => user?.details?.phoneNumber ?? orderPhoneNumber ?? '', [
    user?.details?.phoneNumber,
    orderPhoneNumber,
  ]);

  const priceOrderBaseParams = useMemo(
    () => ({
      appliedOffers,
      rewardsApplied,
      deliveryAddress: deliveryAddress
        ? {
            ...deliveryAddress,
            phoneNumber: orderPhoneNumber ?? '',
          }
        : null,
      orderPhoneNumber,
      deliveryInstructions,
      requestedAmountCents: serverOrder?.cart?.totalCents ?? 0,
      store: priceOrderStoreParam,
      isStoreOpenAndAvailable,
      customerPhoneNumber,
    }),
    [
      appliedOffers,
      rewardsApplied,
      deliveryAddress,
      orderPhoneNumber,
      deliveryInstructions,
      serverOrder?.cart?.totalCents,
      priceOrderStoreParam,
      isStoreOpenAndAvailable,
      customerPhoneNumber,
    ]
  );

  const priceCheckoutOrderForServiceMode = useCallback(
    (mode: ServiceMode) => {
      if (!enableMinimalCartUi) {
        return;
      }

      priceOrder({
        ...priceOrderBaseParams,
        serviceMode: (mode as unknown) as GatewayServiceMode,
      });
    },
    [enableMinimalCartUi, priceOrder, priceOrderBaseParams]
  );

  return { priceCheckoutOrderForServiceMode } as const;
};
